import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import {
  ConfirmComponent,
  ConfirmDialogAction,
  ConfirmDialogData,
} from 'common/common/confirm/confirm.component';
import { BrowserStorageService } from 'common/common/browser-storage.service';
import {
  type BillingNotificationDisplayState,
  shouldPresentNotification,
} from './billing-notification.util';

export interface NotificationMessage {
  readonly key: string;
  readonly params?: Record<string, unknown>;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private static readonly STORAGE_KEY_PREFIX = 'companion.billing-notification';

  private readonly dialog = inject(MatDialog);
  private readonly translateService = inject(TranslateService);
  private readonly browserStorage = inject(BrowserStorageService);

  public readonly notifications = signal<NotificationMessage[]>([]);
  public readonly notificationPreview = signal<NotificationMessage[]>([]);

  public setNotifications(messages: NotificationMessage[]): void {
    this.notifications.set(messages);
  }

  public setPreview(messages: NotificationMessage[]): void {
    this.notificationPreview.set(messages);
  }

  public clear(): void {
    this.notifications.set([]);
    this.notificationPreview.set([]);
  }

  public showHttpError(error: unknown, titleMessageKey: string): void {
    if (!(error instanceof HttpErrorResponse)) {
      return;
    }
    this.dialog.open<ConfirmComponent, ConfirmDialogData, boolean>(ConfirmComponent, {
      data: {
        confirmDialogAction: ConfirmDialogAction.OK,
        customConfirmMessage: this.translateService.instant('general.httpError', {
          code: error.status,
          message: error.message,
        }),
        customConfirmHeadlineKey: titleMessageKey,
      },
    });
  }

  public shouldPresent(tenantId: string, state: BillingNotificationDisplayState): boolean {
    return shouldPresentNotification(state, this.getStoredDisplayState(tenantId));
  }

  public storeDisplayState(tenantId: string, state: BillingNotificationDisplayState): void {
    const key = this.getStorageKey(tenantId);
    this.browserStorage.removeItemsWithPrefix(`${NotificationService.STORAGE_KEY_PREFIX}.`, key);
    this.browserStorage.setItem(key, JSON.stringify(state));
  }

  private getStorageKey(tenantId: string): string {
    return `${NotificationService.STORAGE_KEY_PREFIX}.${tenantId}`;
  }

  private getStoredDisplayState(tenantId: string): BillingNotificationDisplayState | null {
    const rawState = this.browserStorage.getItem(this.getStorageKey(tenantId));
    if (rawState == null) return null;
    try {
      return JSON.parse(rawState) as BillingNotificationDisplayState;
    } catch (error) {
      console.warn('Could not read saved billing notification state.', error);
      return null;
    }
  }
}
